/**
 * Ingredient list commands — fermentables, hops, yeasts, miscs.
 *
 * Four verbs per list, identical in shape and in inverse:
 *
 * | Verb | Inverse |
 * | --- | --- |
 * | `add…(item, index?)` | `remove…(item.itemId)` |
 * | `update…(itemId, changes)` | `update…(itemId, priorValues(changes))` |
 * | `remove…(itemId)` | `add…(priorItem, priorIndex)` |
 * | `reorder…(itemIds)` | `reorder…(priorOrder)` |
 *
 * The blocks below are written out rather than generated from a factory: the
 * types stay exact, `tsc` checks each payload against its own list, and a
 * reader can see the whole contract for hops without decoding a generic.
 */

import type { CatalogRef } from '../../document/common';
import { hasOneProvenance } from '../../document/common';
import type { RecipeMisc } from '../../document/ingredients';
import { defineCommand, inverseCommand } from '../definition';
import { commandError } from '../envelope';
import { contact, mass, num, quoted } from '../format';
import {
  idsOf,
  indexOfId,
  insertAt,
  itemNotFound,
  mergeDefined,
  priorValues,
  removeAt,
  reorderById,
  replaceAt,
  withRecipe,
} from '../helpers';
import * as p from '../payloads';

/** `invalid-state` for a reorder whose id list is not a permutation of the current one. */
function badPermutation(list: string) {
  return commandError(
    'invalid-state',
    `Reordering ${list} needs the complete current set of ids, each exactly once.`,
  );
}

/** `conflict` for an add whose item id is already in the list. */
function duplicateItem(list: string, id: string) {
  return commandError('conflict', `A ${list} with id "${id}" is already in this recipe.`);
}

/**
 * `invalid-state` for a patch that would leave a row both catalog-backed and
 * linked to one of the brewer's own ingredients.
 *
 * The schemas refuse such a row whole, but a patch is checked field by field
 * and only the merged row can be wrong. A swap is not caught by this: it writes
 * both keys in one change set, one of them `null` (see `bridge/replace.ts`).
 */
function twoProvenances(list: string) {
  return commandError(
    'invalid-state',
    `A ${list} is catalog-backed or linked to one of your ingredients, never both; clear one before setting the other.`,
  );
}

/**
 * "Swap Weyermann Pilsner for Rahr", when the update is replacing the
 * ingredient rather than adjusting it.
 *
 * A swap is one `update` command carrying the new entry's snapshot (PLAN-SWAP
 * D-SW1), so it arrives here as an ordinary change set and has to be told apart
 * from an edit. Three things have to hold together:
 *
 * - **the `catalogRef` key is present.** A swap is built by
 *   `replaceFermentable` / `replaceHop`, which spread the fresh row, so the key
 *   is always written — even for a custom-for-custom swap, where it is written
 *   as an explicit `null`. Nothing else writes it. This is the test that keeps
 *   a *rename* out: the mobile ingredient card's title is an editable name
 *   field, and a brewer fixing a typo in their own malt must not read "Swap
 *   Citra for Citrra" in the history.
 * - **the provenance actually moved** — a different id or kind, one side null
 *   and the other not, or a row that had no `catalogRef` to begin with, whose
 *   identity was only ever its name.
 * - **a new name came with it.** A `catalogRef` that moves without one would
 *   produce "Swap Citra for Citra", which says nothing.
 *
 * Returns `null` when this is not a swap, so the caller carries on with its own
 * summary.
 */
function swapLine(
  item: { readonly name: string; readonly catalogRef: CatalogRef | null } | undefined,
  changes: {
    readonly name?: string | undefined;
    readonly catalogRef?: CatalogRef | null | undefined;
  },
): string | null {
  if (item === undefined) return null;
  const name = changes.name;
  if (name === undefined || name === item.name) return null;
  // `undefined` is what an absent key becomes through a JSON round-trip and
  // back out of Zod, so it counts as absent rather than as "clear the ref".
  if (!('catalogRef' in changes) || changes.catalogRef === undefined) return null;
  const replaced = item.catalogRef === null || refsDiffer(item.catalogRef, changes.catalogRef);
  return replaced ? `Swap ${item.name} for ${name}` : null;
}

/** Two provenance stamps naming different catalog entries. Version is not identity. */
function refsDiffer(prior: CatalogRef | null, next: CatalogRef | null): boolean {
  if (prior === null || next === null) return prior !== next;
  return prior.id !== next.id || prior.kind !== next.kind;
}

/** How a misc amount reads in a summary. */
function miscAmount(item: Pick<RecipeMisc, 'amount' | 'unit'>): string {
  return item.unit === 'each' ? `${num(item.amount)} ×` : `${num(item.amount, 2)} ${item.unit}`;
}

// ---------------------------------------------------------------------------
// Fermentables
// ---------------------------------------------------------------------------

/** Add a fermentable, normally the output of `snapshotFermentable`. */
export const recipeAddFermentable = defineCommand({
  type: 'recipe.addFermentable',
  payload: p.recipeAddFermentablePayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => {
      const { item, index } = command.payload;
      if (indexOfId(recipe.fermentables, 'itemId', item.itemId) !== -1) {
        return duplicateItem('fermentable', item.itemId);
      }
      return { ...recipe, fermentables: insertAt(recipe.fermentables, item, index) };
    }),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'recipe.removeFermentable',
          { recipeId: prior.id, itemId: command.payload.item.itemId },
          prior.updatedAt,
        );
  },
  summarize: (_state, command) =>
    `Add ${mass(command.payload.item.amountKg)} ${command.payload.item.name}`,
});

/** Change fields of one fermentable. */
export const recipeUpdateFermentable = defineCommand({
  type: 'recipe.updateFermentable',
  payload: p.recipeUpdateFermentablePayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => {
      const index = indexOfId(recipe.fermentables, 'itemId', command.payload.itemId);
      const item = recipe.fermentables[index];
      if (item === undefined) return itemNotFound('fermentable', command.payload.itemId);
      const merged = mergeDefined(item, command.payload.changes);
      if (!hasOneProvenance(merged)) return twoProvenances('fermentable');
      return { ...recipe, fermentables: replaceAt(recipe.fermentables, index, merged) };
    }),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    const item = prior?.fermentables.find((f) => f.itemId === command.payload.itemId);
    return prior === undefined || item === undefined
      ? null
      : inverseCommand(
          command,
          'recipe.updateFermentable',
          {
            recipeId: prior.id,
            itemId: item.itemId,
            changes: priorValues(item, command.payload.changes),
          },
          prior.updatedAt,
        );
  },
  summarize: (state, command) => {
    const item = state.recipes[command.payload.recipeId]?.fermentables.find(
      (f) => f.itemId === command.payload.itemId,
    );
    const swap = swapLine(item, command.payload.changes);
    if (swap !== null) return swap;
    const name = item?.name ?? command.payload.itemId;
    const amount = command.payload.changes.amountKg;
    if (amount !== undefined && item !== undefined) {
      return `Change ${name} from ${mass(item.amountKg)} to ${mass(amount)}`;
    }
    return `Update ${name} (${Object.keys(command.payload.changes).sort().join(', ')})`;
  },
});

/** Remove one fermentable. */
export const recipeRemoveFermentable = defineCommand({
  type: 'recipe.removeFermentable',
  payload: p.recipeRemoveFermentablePayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => {
      const index = indexOfId(recipe.fermentables, 'itemId', command.payload.itemId);
      if (index === -1) return itemNotFound('fermentable', command.payload.itemId);
      return { ...recipe, fermentables: removeAt(recipe.fermentables, index) };
    }),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    if (prior === undefined) return null;
    const index = indexOfId(prior.fermentables, 'itemId', command.payload.itemId);
    const item = prior.fermentables[index];
    if (item === undefined) return null;
    return inverseCommand(
      command,
      'recipe.addFermentable',
      { recipeId: prior.id, item, index },
      prior.updatedAt,
    );
  },
  summarize: (state, command) => {
    const item = state.recipes[command.payload.recipeId]?.fermentables.find(
      (f) => f.itemId === command.payload.itemId,
    );
    return `Remove ${item === undefined ? command.payload.itemId : `${mass(item.amountKg)} ${item.name}`}`;
  },
});

/** Reorder the fermentable list. */
export const recipeReorderFermentables = defineCommand({
  type: 'recipe.reorderFermentables',
  payload: p.recipeReorderFermentablesPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => {
      const next = reorderById(recipe.fermentables, 'itemId', command.payload.itemIds);
      if (next === null) return badPermutation('fermentables');
      return { ...recipe, fermentables: next };
    }),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'recipe.reorderFermentables',
          { recipeId: prior.id, itemIds: idsOf(prior.fermentables, 'itemId') },
          prior.updatedAt,
        );
  },
  summarize: () => 'Reorder the fermentables',
});

// ---------------------------------------------------------------------------
// Hops
// ---------------------------------------------------------------------------

/** Add a hop addition. */
export const recipeAddHop = defineCommand({
  type: 'recipe.addHop',
  payload: p.recipeAddHopPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => {
      const { item, index } = command.payload;
      if (indexOfId(recipe.hops, 'itemId', item.itemId) !== -1) {
        return duplicateItem('hop addition', item.itemId);
      }
      return { ...recipe, hops: insertAt(recipe.hops, item, index) };
    }),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'recipe.removeHop',
          { recipeId: prior.id, itemId: command.payload.item.itemId },
          prior.updatedAt,
        );
  },
  summarize: (_state, command) => {
    const h = command.payload.item;
    const when =
      h.use === 'dry-hop'
        ? `dry hop${h.dryHopDays === null ? '' : ` ${contact(h.dryHopDays)}`}`
        : h.use === 'first-wort'
          ? 'first wort'
          : `${num(h.timeMinutes)} min ${h.use}`;
    return `Add ${mass(h.amountKg)} ${h.name} at ${when}`;
  },
});

/** Change fields of one hop addition — most often this lot's alpha acid. */
export const recipeUpdateHop = defineCommand({
  type: 'recipe.updateHop',
  payload: p.recipeUpdateHopPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => {
      const index = indexOfId(recipe.hops, 'itemId', command.payload.itemId);
      const item = recipe.hops[index];
      if (item === undefined) return itemNotFound('hop addition', command.payload.itemId);
      const merged = mergeDefined(item, command.payload.changes);
      if (!hasOneProvenance(merged)) return twoProvenances('hop addition');
      return { ...recipe, hops: replaceAt(recipe.hops, index, merged) };
    }),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    const item = prior?.hops.find((h) => h.itemId === command.payload.itemId);
    return prior === undefined || item === undefined
      ? null
      : inverseCommand(
          command,
          'recipe.updateHop',
          {
            recipeId: prior.id,
            itemId: item.itemId,
            changes: priorValues(item, command.payload.changes),
          },
          prior.updatedAt,
        );
  },
  summarize: (state, command) => {
    const item = state.recipes[command.payload.recipeId]?.hops.find(
      (h) => h.itemId === command.payload.itemId,
    );
    const swap = swapLine(item, command.payload.changes);
    if (swap !== null) return swap;
    const name = item?.name ?? command.payload.itemId;
    const { amountKg, alphaAcidPercent } = command.payload.changes;
    if (amountKg !== undefined && item !== undefined) {
      return `Change ${name} from ${mass(item.amountKg)} to ${mass(amountKg)}`;
    }
    if (alphaAcidPercent !== undefined) {
      return `Set ${name} alpha acid to ${num(alphaAcidPercent, 2)} %`;
    }
    return `Update ${name} (${Object.keys(command.payload.changes).sort().join(', ')})`;
  },
});

/** Remove one hop addition. */
export const recipeRemoveHop = defineCommand({
  type: 'recipe.removeHop',
  payload: p.recipeRemoveHopPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => {
      const index = indexOfId(recipe.hops, 'itemId', command.payload.itemId);
      if (index === -1) return itemNotFound('hop addition', command.payload.itemId);
      return { ...recipe, hops: removeAt(recipe.hops, index) };
    }),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    if (prior === undefined) return null;
    const index = indexOfId(prior.hops, 'itemId', command.payload.itemId);
    const item = prior.hops[index];
    if (item === undefined) return null;
    return inverseCommand(
      command,
      'recipe.addHop',
      { recipeId: prior.id, item, index },
      prior.updatedAt,
    );
  },
  summarize: (state, command) => {
    const item = state.recipes[command.payload.recipeId]?.hops.find(
      (h) => h.itemId === command.payload.itemId,
    );
    return `Remove ${item === undefined ? command.payload.itemId : `${mass(item.amountKg)} ${item.name}`}`;
  },
});

/** Reorder the hop list. */
export const recipeReorderHops = defineCommand({
  type: 'recipe.reorderHops',
  payload: p.recipeReorderHopsPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => {
      const next = reorderById(recipe.hops, 'itemId', command.payload.itemIds);
      if (next === null) return badPermutation('hop additions');
      return { ...recipe, hops: next };
    }),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'recipe.reorderHops',
          { recipeId: prior.id, itemIds: idsOf(prior.hops, 'itemId') },
          prior.updatedAt,
        );
  },
  summarize: () => 'Reorder the hop schedule',
});

// ---------------------------------------------------------------------------
// Yeasts
// ---------------------------------------------------------------------------

/** Add a yeast pitch. */
export const recipeAddYeast = defineCommand({
  type: 'recipe.addYeast',
  payload: p.recipeAddYeastPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => {
      const { item, index } = command.payload;
      if (indexOfId(recipe.yeasts, 'itemId', item.itemId) !== -1) {
        return duplicateItem('yeast', item.itemId);
      }
      return { ...recipe, yeasts: insertAt(recipe.yeasts, item, index) };
    }),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'recipe.removeYeast',
          { recipeId: prior.id, itemId: command.payload.item.itemId },
          prior.updatedAt,
        );
  },
  summarize: (_state, command) => `Add yeast ${command.payload.item.name}`,
});

/** Change fields of one yeast pitch. */
export const recipeUpdateYeast = defineCommand({
  type: 'recipe.updateYeast',
  payload: p.recipeUpdateYeastPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => {
      const index = indexOfId(recipe.yeasts, 'itemId', command.payload.itemId);
      const item = recipe.yeasts[index];
      if (item === undefined) return itemNotFound('yeast', command.payload.itemId);
      const merged = mergeDefined(item, command.payload.changes);
      if (!hasOneProvenance(merged)) return twoProvenances('yeast');
      return { ...recipe, yeasts: replaceAt(recipe.yeasts, index, merged) };
    }),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    const item = prior?.yeasts.find((y) => y.itemId === command.payload.itemId);
    return prior === undefined || item === undefined
      ? null
      : inverseCommand(
          command,
          'recipe.updateYeast',
          {
            recipeId: prior.id,
            itemId: item.itemId,
            changes: priorValues(item, command.payload.changes),
          },
          prior.updatedAt,
        );
  },
  summarize: (state, command) => {
    const item = state.recipes[command.payload.recipeId]?.yeasts.find(
      (y) => y.itemId === command.payload.itemId,
    );
    const name = item?.name ?? command.payload.itemId;
    const attenuation = command.payload.changes.attenuationPercentOverride;
    if (attenuation !== undefined && attenuation !== null) {
      return `Set ${name} attenuation to ${num(attenuation, 1)} %`;
    }
    return `Update ${name} (${Object.keys(command.payload.changes).sort().join(', ')})`;
  },
});

/** Remove one yeast pitch. */
export const recipeRemoveYeast = defineCommand({
  type: 'recipe.removeYeast',
  payload: p.recipeRemoveYeastPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => {
      const index = indexOfId(recipe.yeasts, 'itemId', command.payload.itemId);
      if (index === -1) return itemNotFound('yeast', command.payload.itemId);
      return { ...recipe, yeasts: removeAt(recipe.yeasts, index) };
    }),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    if (prior === undefined) return null;
    const index = indexOfId(prior.yeasts, 'itemId', command.payload.itemId);
    const item = prior.yeasts[index];
    if (item === undefined) return null;
    return inverseCommand(
      command,
      'recipe.addYeast',
      { recipeId: prior.id, item, index },
      prior.updatedAt,
    );
  },
  summarize: (state, command) => {
    const item = state.recipes[command.payload.recipeId]?.yeasts.find(
      (y) => y.itemId === command.payload.itemId,
    );
    return `Remove yeast ${item?.name ?? command.payload.itemId}`;
  },
});

/** Reorder the yeast list. */
export const recipeReorderYeasts = defineCommand({
  type: 'recipe.reorderYeasts',
  payload: p.recipeReorderYeastsPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => {
      const next = reorderById(recipe.yeasts, 'itemId', command.payload.itemIds);
      if (next === null) return badPermutation('yeasts');
      return { ...recipe, yeasts: next };
    }),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'recipe.reorderYeasts',
          { recipeId: prior.id, itemIds: idsOf(prior.yeasts, 'itemId') },
          prior.updatedAt,
        );
  },
  summarize: () => 'Reorder the yeasts',
});

// ---------------------------------------------------------------------------
// Miscs
// ---------------------------------------------------------------------------

/** Add a miscellaneous addition. */
export const recipeAddMisc = defineCommand({
  type: 'recipe.addMisc',
  payload: p.recipeAddMiscPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => {
      const { item, index } = command.payload;
      if (indexOfId(recipe.miscs, 'itemId', item.itemId) !== -1) {
        return duplicateItem('misc addition', item.itemId);
      }
      return { ...recipe, miscs: insertAt(recipe.miscs, item, index) };
    }),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'recipe.removeMisc',
          { recipeId: prior.id, itemId: command.payload.item.itemId },
          prior.updatedAt,
        );
  },
  summarize: (_state, command) =>
    `Add ${miscAmount(command.payload.item)} ${command.payload.item.name} at ${command.payload.item.use}`,
});

/** Change fields of one miscellaneous addition. */
export const recipeUpdateMisc = defineCommand({
  type: 'recipe.updateMisc',
  payload: p.recipeUpdateMiscPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => {
      const index = indexOfId(recipe.miscs, 'itemId', command.payload.itemId);
      const item = recipe.miscs[index];
      if (item === undefined) return itemNotFound('misc addition', command.payload.itemId);
      const merged = mergeDefined(item, command.payload.changes);
      if (!hasOneProvenance(merged)) return twoProvenances('misc addition');
      return { ...recipe, miscs: replaceAt(recipe.miscs, index, merged) };
    }),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    const item = prior?.miscs.find((m) => m.itemId === command.payload.itemId);
    return prior === undefined || item === undefined
      ? null
      : inverseCommand(
          command,
          'recipe.updateMisc',
          {
            recipeId: prior.id,
            itemId: item.itemId,
            changes: priorValues(item, command.payload.changes),
          },
          prior.updatedAt,
        );
  },
  summarize: (state, command) => {
    const item = state.recipes[command.payload.recipeId]?.miscs.find(
      (m) => m.itemId === command.payload.itemId,
    );
    const name = item?.name ?? command.payload.itemId;
    const amount = command.payload.changes.amount;
    if (amount !== undefined && item !== undefined) {
      return `Change ${name} to ${miscAmount({ amount, unit: command.payload.changes.unit ?? item.unit })}`;
    }
    return `Update ${name} (${Object.keys(command.payload.changes).sort().join(', ')})`;
  },
});

/** Remove one miscellaneous addition. */
export const recipeRemoveMisc = defineCommand({
  type: 'recipe.removeMisc',
  payload: p.recipeRemoveMiscPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => {
      const index = indexOfId(recipe.miscs, 'itemId', command.payload.itemId);
      if (index === -1) return itemNotFound('misc addition', command.payload.itemId);
      return { ...recipe, miscs: removeAt(recipe.miscs, index) };
    }),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    if (prior === undefined) return null;
    const index = indexOfId(prior.miscs, 'itemId', command.payload.itemId);
    const item = prior.miscs[index];
    if (item === undefined) return null;
    return inverseCommand(
      command,
      'recipe.addMisc',
      { recipeId: prior.id, item, index },
      prior.updatedAt,
    );
  },
  summarize: (state, command) => {
    const item = state.recipes[command.payload.recipeId]?.miscs.find(
      (m) => m.itemId === command.payload.itemId,
    );
    return `Remove ${item === undefined ? command.payload.itemId : quoted(item.name)}`;
  },
});

/** Reorder the misc list. */
export const recipeReorderMiscs = defineCommand({
  type: 'recipe.reorderMiscs',
  payload: p.recipeReorderMiscsPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => {
      const next = reorderById(recipe.miscs, 'itemId', command.payload.itemIds);
      if (next === null) return badPermutation('misc additions');
      return { ...recipe, miscs: next };
    }),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'recipe.reorderMiscs',
          { recipeId: prior.id, itemIds: idsOf(prior.miscs, 'itemId') },
          prior.updatedAt,
        );
  },
  summarize: () => 'Reorder the misc additions',
});
